"use client";

import { type MouseEvent, type ReactElement } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import { getEventColorClasses } from "../event-colors";
import { POPOVER_CLASSES } from "../styles";
import { type CalendarEvent } from "../types";
import { formatEventWhen } from "../utils";

// =============================================================================

/** The portal bubbles through the React tree: keep clicks off the cells */
function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}

type EventPopoverProps = {
  event: CalendarEvent;
  /** The block or bar acting as trigger (a button) */
  children: ReactElement;
};

/** Opens the event details on click and reports the event to the caller */
export function EventPopover({ event, children }: EventPopoverProps) {
  const { onEventClick } = useCalendarContext();

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) onEventClick?.(event);
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className={cn(POPOVER_CLASSES, "p-0")}
        onClick={stopPropagation}
      >
        <EventDetail event={event} />
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================

/** Title, time and the caller's custom content */
export function EventDetail({ event }: { event: CalendarEvent }) {
  const { locale, renderEventDetail } = useCalendarContext();
  const detail = renderEventDetail?.(event);

  return (
    <div className="p-3">
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className={cn(
            "mt-1.5 size-2 shrink-0 rounded-full",
            getEventColorClasses(event.color).dot
          )}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium break-words text-foreground">
            {event.title}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatEventWhen(event, locale)}
          </p>
        </div>
      </div>
      {detail != null && detail !== false && (
        <div className="mt-3 border-t border-line pt-3 text-sm">{detail}</div>
      )}
    </div>
  );
}
